import React, { useState } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { useLogger } from '@/hooks/useLogger';

export const EmailLogin: React.FC = () => {
    const { signInWithPassword } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const logger = useLogger('AuthFlow');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            logger.info('Login attempt started', { email });
            await signInWithPassword(email, password);
            logger.info('Login successful', { email });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'שגיאת זיהוי';
            logger.error('Login failed', err, { email });
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full" dir="rtl">
            {error && (
                <div className="bg-red-500/10 text-red-400 border border-red-500/20 p-3 rounded-2xl text-sm text-center">
                    {error}
                </div>
            )}

            <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-mystic-400 w-5 h-5 pointer-events-none" />
                <input
                    type="email"
                    placeholder="אימייל"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pr-10 pl-4 py-3 bg-white/5 border border-white/10 rounded-2xl
                               text-mystic-100 placeholder:text-mystic-500
                               focus:ring-2 focus:ring-gold-400/50 focus:border-transparent
                               outline-none transition-all"
                    required
                />
            </div>

            <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-mystic-400 w-5 h-5 pointer-events-none" />
                <input
                    type="password"
                    placeholder="סיסמה"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pr-10 pl-4 py-3 bg-white/5 border border-white/10 rounded-2xl
                               text-mystic-100 placeholder:text-mystic-500
                               focus:ring-2 focus:ring-gold-400/50 focus:border-transparent
                               outline-none transition-all"
                    required
                />
            </div>

            <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gold-400 hover:bg-gold-500 text-mystic-900 font-bold
                           rounded-2xl shadow-lg shadow-gold-400/20
                           transition-all duration-300 hover:-translate-y-0.5 active:scale-95
                           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0
                           flex items-center justify-center gap-2"
            >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'כניסה'}
            </button>
        </form>
    );
};
